import { countSearchParams } from "@/_components/AdvancedSearch"
import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import CustomButton from "@/_components/CustomButton"
import FormInput from "@/_components/FormInput"
import FormInputExtended from "@/_components/FormInputExtended"
import PanelOuter from "@/_components/PanelOuter"
import PersonLink from "@/_components/PersonLink"
import Table, { mergeColumns } from "@/_components/Table"
import TableExtended from "@/_components/TableExtended"
import { filterData } from "@/_services/boardUtils"
import { getEntityDefaultConfig, getEntitySearchFields, toFieldNames } from "@/_services/entity"
import { loc } from "@/_services/localization"
import { addOops } from "@/_services/notification"
import { getIsAdmin, getPageConfig } from "@/_services/userConfiguration"
import { getEntities, getEntity, pseudoSelectOptionClassName, searchEntities } from "@/_services/utils"
import { isEqual, replaceVariables, YYYYMMDD } from "basikon-common-utils"
import get from "lodash.get"
import React from "react"
import { Col, Modal, Row } from "react-bootstrap"
import { Link } from "react-router-dom"

/**
 * @prop {String}   entityName                      Name of the entity (camelCase + starts with a capital letter)
 * @prop {[{}]}     query                           Query params object to be applied when fetching the entities
 * @prop {[{}]}     columns                         Array of columns to override standard entity search columns
 * @prop {Boolean}  advancedSearch.collapse         Collapse advanced search panel if true                      Default to undefined.
 * @prop {Boolean}  advancedSearch.hidden           Hide advanced search panel if true                          Default to false.
 * @prop {[{}]}     advancedSearch.fields           Array of fields to override standard entity search fields
 * @prop {[{}]}     advancedSearch.additionalFields Array of additional fields to be added after the standard entity search fields
 * @prop {{}}       advancedSearch.props_{field}    Object to apply field props on {field}
 * @prop {{}}       subSelection                    Opt-in: let the user expand each row to pick a subset of an array field (ex: a product's schemes).
 * @prop {String}   subSelection.field              Entity array field holding the sub-items (ex: "schemes")
 * @prop {String}   subSelection.idField            Sub-item identifier field (ex: "code")
 * @prop {Number}   subSelection.pageSize           Sub-items per page in the nested table                      Default to 7.
 * @prop {[{}]}     subSelection.columns            Columns override for the nested sub-items table
 */
class EntitySearchModal extends React.Component {
  constructor(props) {
    super(props)
    const { params = {}, advancedSearch, columns } = props
    const entityName = props.entityName || props.entity // Remove support for "entity" prop after migrating all scripts

    let collapse = true
    if (advancedSearch?.collapse !== undefined) collapse = advancedSearch.collapse

    let entityColumns
    if (columns) {
      entityColumns = Array.isArray(columns) ? columns : typeof columns === "string" ? columns.split(",") : undefined
      if (Array.isArray(entityColumns)) entityColumns = entityColumns.filter(col => col).map(col => (typeof col === "string" ? { name: col } : col))
    }
    if (!entityColumns) {
      const { columns } = getEntityDefaultConfig(entityName)
      const pageConfig = entityName ? getPageConfig(`${entityName}sPage`) : {}
      entityColumns = mergeColumns(columns, pageConfig.columns)
    }

    this.state = {
      showSearchHint: false,
      collapse,
      entityName,
      entities: [],
      selectedEntities: props.multiple ? new Set(JSON.parse(JSON.stringify([...(props.selectedEntities || [])]))) : undefined,
      form: { ...params },
      isLoading: false,
      entityColumns,
      // query can contain non-filters also like include, projection, etc..
      query: { ...(props.filters || {}), ...(props.query || {}) }, // TODO: Remove support for "filters" prop after migrating all scripts
      paginationParams: undefined,
      searchInputId: `search-entity-${entityName}`,
      subSelections: new Map(),
    }
  }

  componentDidMount() {
    this.getEntities()
  }

  componentDidUpdate(prevProps) {
    const prevQuery = { ...(prevProps?.filters || {}), ...(prevProps?.query || {}) } // TODO: Remove support for "filters" prop after migrating all scripts
    const query = { ...(this.props.filters || {}), ...(this.props.query || {}) } // TODO: Remove support for "filters" prop after migrating all scripts

    if (!isEqual(prevQuery, query)) this.setState({ query })
  }

  getEntities = async ({ nextPagination } = {}) => {
    const { entityName, query, entityColumns, entities } = this.state
    const { customGetEntities } = this.props

    this.setState({ isLoading: true })

    const queryParams = { ...(query || {}) }
    if (entityColumns.find(it => it.name === "organization.name" && !it.hidden) && !queryParams.include) queryParams.include = "organizationName"

    if (nextPagination) {
      const { data: moreEntities, paginationParams } = await getEntities(entityName, queryParams, { nextPagination })
      return this.setState({ entities: [...entities, ...moreEntities], paginationParams, isLoading: false })
    }

    const patch = { isLoading: false }
    try {
      if (customGetEntities) patch.entities = await customGetEntities(queryParams)
      else {
        const { data, paginationParams } = await getEntities(entityName, queryParams)
        patch.entities = data
        patch.paginationParams = paginationParams
      }
      this.setState(patch)
    } catch (error) {
      addOops(error)
      this.setState(patch)
    }
  }

  buildQueryParams = (baseParams = {}) => {
    const { form = {} } = this.state
    const searchFields = this.getSearchFields()
    const fieldNames = toFieldNames(searchFields)

    const queryParams = { ...baseParams }
    for (const fieldName of fieldNames) {
      const value = get(form, fieldName)
      if (value === undefined) continue

      const fieldProps = searchFields.flat().find(f => f.field === fieldName)
      const fieldRegex = fieldProps?.regex

      if (fieldRegex && value) {
        if (typeof fieldRegex === "object") {
          const { fromStart, honorCase } = fieldRegex
          queryParams[fieldName] = `/${fromStart ? "^" : ""}${value}${honorCase ? "" : "/i"}`
        } else {
          queryParams[fieldName] = `/${value}/i`
        }
      } else if (value instanceof Date) {
        queryParams[fieldName] = YYYYMMDD(value)
      } else if (value || value === false || value === 0) {
        queryParams[fieldName] = value
      } else {
        queryParams[fieldName] = null
      }
    }

    return queryParams
  }

  searchEntities = async () => {
    const { entityColumns, entityName, query } = this.state
    const { customSearch } = this.props

    const baseParams = { ...(query || {}), search: undefined }
    if (entityColumns.find(it => it.name === "organization.name" && !it.hidden) && !baseParams.include) baseParams.include = "organizationName"

    const queryParams = this.buildQueryParams(baseParams)

    this.setState({ isLoading: true })

    const patch = { isLoading: false, collapse: true }
    try {
      if (customSearch) patch.entities = await customSearch(queryParams)
      else {
        const { data, paginationParams } = await getEntities(entityName, queryParams)
        patch.entities = data
        patch.paginationParams = paginationParams
      }
      this.setState(patch)
    } catch (error) {
      addOops(error)
      this.setState(patch)
    }
  }

  quickSearchEntities = async () => {
    const { entityName, query, search = "" } = this.state
    const { customQuickSearch } = this.props

    this.setState({ isLoading: true })

    try {
      const queryParams = this.buildQueryParams(query)
      const entities = customQuickSearch ? await customQuickSearch(search, queryParams) : await searchEntities(entityName, search, queryParams)
      this.setState({ entities, isLoading: false, collapse: true })
    } catch (error) {
      addOops(error)
      this.setState({ isLoading: false, collapse: true })
    }
  }

  handleClose = async selectedEntity => {
    const { entityName, selectedEntities } = this.state
    const { include, onClose, multiple, subSelection } = this.props

    let selectedData
    if (selectedEntity) {
      const registration = selectedEntity.registration?.props?.children || selectedEntity.registration
      selectedData = registration ? await getEntity(entityName, registration, { include }) : selectedEntity
      if (subSelection && selectedData) selectedData = this.applySubSelection(selectedData)
    } else if (multiple) {
      selectedData = await Promise.all(Array.from(selectedEntities).map(registration => getEntity(entityName, registration, { include })))
      if (subSelection) selectedData = selectedData.map(entity => this.applySubSelection(entity))
    }

    if (onClose) onClose(selectedData)
  }

  handleSearchKeyDown = async () => {
    const { search = "", showSearchHint } = this.state

    if (search.trim()) {
      this.quickSearchEntities()
    } else {
      this.getEntities()
    }

    if (showSearchHint) {
      this.setState({ showSearchHint: false })
    }
  }

  handleSetFormState = patch => {
    const { form = {} } = this.state
    this.setState({ form: { ...form, ...patch } })
  }

  resetSearchFields = () => {
    const { onSearchFieldsReset } = this.props
    if (onSearchFieldsReset) onSearchFieldsReset()
    this.setState({ form: {} })
  }

  handleMultipleSelection = entity => {
    const { selectedEntities } = this.state
    if (selectedEntities.has(entity.id)) {
      selectedEntities.delete(entity.id)
    } else {
      selectedEntities.add(entity.id)
    }
    this.setState({ selectedEntities })
  }

  handleSelectAll = () => {
    const { entities = [], search = "", selectedEntities } = this.state
    const { entityColumns } = this.state

    const columns = entityColumns.filter(col => !col.hidden).map(col => ({ title: col.title, name: col.name }))
    const data = entities.map(entity => {
      const row = { id: entity.registration, _data: entity }
      entityColumns.forEach(col => {
        const { name, field = name } = col
        row[name] = get(entity, field)
      })
      return row
    })

    const filteredData = filterData(columns, data, search)
    const visibleIds = new Set(filteredData.map(row => row.id))

    // Check if all visible entities are selected
    const allSelected = filteredData.length > 0 && filteredData.every(row => selectedEntities.has(row.id))

    if (allSelected) {
      // Deselect all visible entities
      visibleIds.forEach(id => selectedEntities.delete(id))
    } else {
      // Select all visible entities
      visibleIds.forEach(id => selectedEntities.add(id))
    }

    this.setState({ selectedEntities: new Set(selectedEntities) })
  }

  // Selected sub-item ids for an entity. No entry means "all selected" (the default).
  getSelectedSubIds = entity => {
    const { subSelection } = this.props
    const { subSelections } = this.state

    if (subSelections.has(entity?.registration)) return subSelections.get(entity.registration)
    return new Set((entity?.[subSelection.field] || []).map(it => it[subSelection.idField]))
  }

  // In multiple mode, touching an entity's sub-items implies selecting that entity, so auto-select it.
  autoSelectEntityPatch = entity => {
    const { multiple } = this.props
    const { selectedEntities } = this.state
    if (!multiple || !selectedEntities || selectedEntities.has(entity.registration)) return {}
    return { selectedEntities: new Set(selectedEntities).add(entity.registration) }
  }

  handleToggleSubItem = (entity, subId, checked) => {
    const selected = new Set(this.getSelectedSubIds(entity))
    if (checked) selected.add(subId)
    else selected.delete(subId)
    const subSelections = new Map(this.state.subSelections)
    subSelections.set(entity.registration, selected)
    this.setState({ subSelections, ...this.autoSelectEntityPatch(entity) })
  }

  handleToggleAllSubItems = (entity, checked) => {
    const { subSelection } = this.props

    const all = new Set((entity?.[subSelection.field] || []).map(it => it[subSelection.idField]))
    const subSelections = new Map(this.state.subSelections)
    subSelections.set(entity.registration, checked ? all : new Set())
    this.setState({ subSelections, ...this.autoSelectEntityPatch(entity) })
  }

  // Filter a returned entity's sub-items down to the user's selection (no entry = keep all).
  applySubSelection = entity => {
    const { subSelection } = this.props
    const { field, idField } = subSelection
    const selected = this.state.subSelections.get(entity?.registration)
    if (!selected) return entity
    return { ...entity, [field]: (entity[field] || []).filter(it => selected.has(it[idField])) }
  }

  renderSubSelectionTable = entity => {
    const { subSelection } = this.props
    const { idField, pageSize, columns = [] } = subSelection
    const subItems = entity?.[subSelection.field] || []
    const selectedIds = this.getSelectedSubIds(entity)
    const allChecked = subItems.length > 0 && subItems.every(it => selectedIds.has(it[idField]))

    return (
      <Table
        className="mb-0"
        pageInUrl={false}
        pageSize={pageSize}
        columns={[
          {
            name: "__select",
            sortable: false,
            className: "w-5",
            title: (
              <FormInput
                inArray
                showLabel={false}
                type="checkbox"
                obj={{ checked: allChecked }}
                field="checked"
                onSetState={patch => this.handleToggleAllSubItems(entity, patch.checked)}
              />
            ),
          },
          ...columns,
        ]}
        data={subItems.map(item => ({
          ...item,
          __rowKey: String(item[idField]),
          __select: (
            <FormInput
              inArray
              showLabel={false}
              type="checkbox"
              obj={{ checked: selectedIds.has(item[idField]) }}
              field="checked"
              onSetState={patch => this.handleToggleSubItem(entity, item[idField], patch.checked)}
            />
          ),
        }))}
      />
    )
  }

  getSearchFields = () => {
    const { entityName } = this.state
    const { advancedSearch } = this.props

    // Custom/Additional search fields
    let searchFields = advancedSearch?.fields || getEntitySearchFields(entityName)
    if (Array.isArray(advancedSearch?.additionalFields)) searchFields = [...searchFields, ...advancedSearch.additionalFields]

    return searchFields
  }

  renderAdvancedSearch() {
    const { form = {}, collapse, query } = this.state
    const { advancedSearch } = this.props

    const formAndFilters = { ...query, ...form }

    // Apply field props
    for (const key of Object.keys(advancedSearch || {}).filter(key => key?.startsWith("props_"))) {
      formAndFilters[key] = { ...(formAndFilters[key] || {}), ...(advancedSearch[key] || {}) }
    }

    // Custom/Additional search fields
    const searchFields = this.getSearchFields().map((fieldsArray, rowIndex) => {
      const fieldCols = (fieldsArray || [])
        .map((formInputProps, index) => {
          let { col, colProps, legend, field, hidden, ...props } = formInputProps
          colProps = colProps || col || { xs: 12, sm: 6, lg: 4 } // TODO: Remove "col" when all scripts are migrated

          if (hidden === true) return null
          if (legend) {
            return (
              <Col xs={12} key={index}>
                <legend className="font-2 mt-2">{loc(legend)}</legend>
              </Col>
            )
          }

          const disabled =
            formAndFilters?.[`props_${field}`]?.disabled !== undefined ? formAndFilters[`props_${field}`].disabled : query?.[field] && !getIsAdmin()
          const multiple = formInputProps.type === "multiple" || formInputProps.multiple
          const handleNegativeValues = multiple ? true : undefined

          const value = multiple
            ? Array.isArray(formAndFilters[field])
              ? formAndFilters[field]
              : (formAndFilters[field] || "").split(",")
            : formAndFilters[field]

          return (
            <FormInputExtended
              {...{ field, colProps, disabled, multiple, handleNegativeValues, ...props }}
              key={index}
              obj={formAndFilters}
              value={value}
              onEnter={this.searchEntities}
              onSetState={this.handleSetFormState}
            />
          )
        })
        .filter(field => field)

      return <Row key={rowIndex}>{fieldCols}</Row>
    })

    return (
      <Col xs={12}>
        <PanelOuter expanded={collapse !== true} onToggle={() => this.setState({ collapse: !collapse })}>
          <legend className="c-pointer mt-theme" onClick={() => this.setState({ collapse: !collapse })}>{loc`Advanced search`}</legend>
          {searchFields}

          <Row>
            <Col xs={12} className="mb-theme text-right">
              <CustomButton bsStyle="danger" bsSize="sm" className="mr-10px" fill onClick={this.resetSearchFields}>
                {loc`Reset`}
              </CustomButton>
              <CustomButton bsStyle="primary" bsSize="sm" fill onClick={this.searchEntities}>
                {loc`Search`}
              </CustomButton>
            </Col>
          </Row>
        </PanelOuter>
      </Col>
    )
  }

  setSearch = patch => {
    const { showSearchHint } = this.state
    const { search } = patch
    const statePatch = { search }
    if (!showSearchHint) statePatch.showSearchHint = true
    this.setState(statePatch)
  }

  handleClear = async () => {
    const { searchInputId } = this.state
    this.setState({ search: "", showSearchHint: false }, () => {
      document.getElementsByClassName(searchInputId)[0].focus()
    })
  }

  render() {
    const {
      showSearchHint,
      form = {},
      entities = [],
      search = "",
      searchInputId,
      collapse,
      selectedEntities,
      entityColumns,
      entityName,
      query,
      paginationParams,
    } = this.state
    const { title, multiple, onClose, advancedSearch, canQuickSearch = true, showAdd, subSelection } = this.props

    const isAdvancedSearchHidden = advancedSearch?.hidden === true
    const isLoading = this.state.isLoading || this.props.isLoading

    const formAndFilters = { ...(query || {}), ...form }
    const searchFieldsNb = countSearchParams(formAndFilters, entityName)

    const columns = entityColumns.filter(col => !col.hidden).map(col => ({ title: col.title, name: col.name }))
    const data = entities.map(entity => {
      const row = { id: entity.registration, _data: entity }

      entityColumns.forEach(col => {
        const { name, field = name, link, currencyPath, findIn, findKey, findValue, foundKey, ...props } = col

        if (link) {
          if (findIn && findKey && findValue && foundKey) {
            const found = entity[findIn]?.find(it => it[findKey] === findValue)
            const foundValue = found?.[foundKey]
            if (found && foundValue) {
              row[name] = (
                <Link to={replaceVariables(link, found)} target="_blank">
                  {get(found, field)}
                </Link>
              )
            }
          } else if (typeof link === "string" && link.includes("/person/")) {
            row[name] = <PersonLink registration={get(entity, field)} target="_blank" />
          } else if (typeof link === "string") {
            row[name] = (
              <Link to={replaceVariables(link, entity)} target="_blank">
                {get(entity, field)}
              </Link>
            )
          }
        } else {
          row[name] = (
            <FormInput
              inArray
              readOnly
              obj={entity}
              field={field}
              currency={get(entity, currencyPath)}
              {...props}
              openLinkInNewTab={props.openLinkInNewTab ?? !!props.linkTo}
            />
          )
        }
      })

      // Opt-in: an expandable nested table to pick a subset of an array field (ex: the product's schemes).
      if (subSelection) row.children = this.renderSubSelectionTable(entity)

      return row
    })

    const filteredData = filterData(columns, data, search)
    const hasSelectedEntities = multiple && selectedEntities && selectedEntities.size > 0

    return (
      <Modal show={true} onHide={() => !isLoading && onClose()} bsSize="large" backdrop="static" className="entity-search-modal">
        <Modal.Header closeButton>
          <Modal.Title className="inline-flex align-items-center">
            {loc(title || `Search ${entityName?.toLowerCase()}`)}
            {isLoading && <i className="ml-10px icn-circle-notch icn-spin icn-sm text-primary" />}
          </Modal.Title>
          <div className="float-right">
            {hasSelectedEntities && (
              <CustomButton bsSize="sm" bsStyle="primary" fill onClick={this.handleSelectAll} disabled={isLoading}>
                {filteredData.every(row => selectedEntities?.has(row.id)) ? loc`Unselect all` : loc`Select all`}
              </CustomButton>
            )}
            {showAdd && (
              <>
                {" "}
                <CustomButton bsSize="sm" bsStyle="info" fill onClick={() => onClose("add")}>
                  {loc(showAdd.label || `Add`)}
                </CustomButton>
              </>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col
              xs={isAdvancedSearchHidden ? 12 : 10}
              sm={isAdvancedSearchHidden ? 12 : 11}
              className={`pseudo-select-control ${isAdvancedSearchHidden ? " pdb-4" : ""}`}
            >
              <div className="relative">
                <FormInput
                  field="search"
                  inArray
                  value={search}
                  debounce
                  fcClassName={searchInputId}
                  onKeyDown={event => {
                    if (!canQuickSearch || event.key !== "Enter") return
                    this.handleSearchKeyDown()
                  }}
                  onSetState={this.setSearch}
                  placeholder={loc("Search...")}
                />

                {/* This is almost the same as SearchRowComponent */}
                {showSearchHint && search && (
                  <div className="pseudo-select-options">
                    <div
                      onMouseDown={event => {
                        if (event.button !== 0) return
                        this.handleSearchKeyDown()
                      }}
                      className={`leading-1-5 ${pseudoSelectOptionClassName}`}
                      data-has-icon="true"
                    >
                      <span className="flex-center">
                        <i className="icn-search icn-sm text-gray-darker" />
                        <span className="pdl-5px text-black">{search}</span>
                      </span>
                      <span className="pdl-5px text-black font-1-2">{loc(`Tap ENTER for more results`)}</span>
                    </div>
                  </div>
                )}

                {search && (
                  <div className="pseudo-select-addons" onClick={this.handleClear}>
                    <ButtonWithTooltip
                      bsStyle="default"
                      className="icn-xmark icn-xs flex-center"
                      btnClassName="m-0 min-w-0 text-gray-darker flex-center"
                      onClick={this.handleClear}
                      tooltip={"Clear"}
                    />
                  </div>
                )}
              </div>
            </Col>
            {!isAdvancedSearchHidden && (
              <Col xs={2} sm={1} className="flex">
                <ButtonWithTooltip
                  bsStyle="default"
                  className="icn-search-filter"
                  btnClassName="flex-center"
                  data-test="advanced-search-btn"
                  onClick={() => this.setState({ collapse: !collapse })}
                  tooltip={"Toggle search filters"}
                  statusIndicator={searchFieldsNb ? "info" : ""}
                />
              </Col>
            )}
            {!isAdvancedSearchHidden && this.renderAdvancedSearch()}
          </Row>

          <Row className="mt-10px">
            <Col xs={12}>
              <TableExtended
                overflowX
                showRowsCount
                data-test="search-entity-modal-table"
                columns={columns}
                data={filteredData}
                pageSize={10}
                pageInUrl={false}
                getEntities={this.getEntities}
                paginationParams={paginationParams}
                selectedEntities={selectedEntities}
                onRowClick={dataRow => !isLoading && (multiple ? this.handleMultipleSelection(dataRow) : this.handleClose(dataRow._data))}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <CustomButton bsSize="sm" disabled={isLoading} onClick={() => onClose()}>{loc`Cancel`}</CustomButton>

          {/* no selection is a valid selection, so no check on it to disable / hide this button */}
          {multiple && (
            <CustomButton disabled={isLoading} bsStyle="primary" bsSize="sm" fill onClick={() => this.handleClose()}>{loc`Validate`}</CustomButton>
          )}
        </Modal.Footer>
      </Modal>
    )
  }
}

export default EntitySearchModal
